import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { AuthStore } from '@wheere-front/shared-auth';
import {
  BugReportButtonComponent,
  ConsoleCaptureService,
  LanguageService,
  ThemeService,
} from '@wheere-front/shared-ui';
import { MenuItem, MessageService } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { KeycloakService } from 'keycloak-angular';

interface CustomerNavTab {
  id: 'sites';
  labelKey: string;
  color: string;
  link: string;
  /** Roles allowed to see the tab. Empty array = visible to everyone in CUSTOMER_ROLES. */
  visibleFor: string[];
}

/**
 * Customer-side app shell. Mirrors wheere-integrator's AppComponent chrome
 * so the customer app feels consistent with the maintainer app: sticky
 * header, color-coded nav tabs, dark-mode + language toggles, mobile
 * hamburger + drawer that auto-closes on navigation, polished avatar menu
 * with copy-support + logout, and two PrimeNG toast outlets.
 *
 * Differences vs. integrator (intentional):
 * - **No admin-scope selector** — the customer app is single-org.
 * - **No bug-report / console-capture buttons** — customer support comes from
 *   the support email exposed via the avatar menu.
 * - **No command palette** — the customer surface is too small to justify it
 *   in v1 (sites + per-site map). Plan to revisit once the user count grows.
 * - **Tab palette is smaller** — Sites (electricPurple) + Users (aquamarine).
 *   Mirrors integrator's color legend so a maintainer switching apps doesn't
 *   re-learn the chrome.
 *
 * Slots: route content goes in {@code <router-outlet />} via content
 * projection — consumers wrap their root with:
 * ```html
 * <lib-customer-shell>
 *   <router-outlet />
 * </lib-customer-shell>
 * ```
 */
@Component({
  selector: 'lib-customer-shell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, RouterLinkActive, TranslocoModule, Menu, BugReportButtonComponent],
  styles: [
    `
      :host {
        display: block;
        font-family: 'Lato', ui-sans-serif, system-ui, sans-serif;
      }
    `,
  ],
  template: `
    <div class="relative flex min-h-screen flex-col bg-gray-50/75 text-gray-900 dark:bg-slate-900 dark:text-slate-100">
      <!-- ── Header ─────────────────────────────────────────────────────────
        - mobile  → hamburger | logo | theme + avatar
        - desktop → logo | inline nav | language + theme + avatar
        ──────────────────────────────────────────────────────────────────── -->
      <header
        class="sticky top-0 z-10 flex h-14 items-center justify-between gap-3 border-b border-gray-200 bg-white px-3 md:px-5 dark:border-slate-700 dark:bg-slate-800">
        <div class="flex min-w-0 items-center gap-3 md:gap-6">
          <!-- Hamburger (mobile only) — toggles the drawer below the header. -->
          <button
            type="button"
            class="-ml-1 inline-flex size-9 shrink-0 items-center justify-center rounded-md text-gray-700 hover:bg-gray-100 active:bg-gray-200 md:hidden dark:text-slate-200 dark:hover:bg-slate-700 dark:active:bg-slate-600"
            [attr.aria-expanded]="mobileMenuOpen()"
            aria-controls="customer-nav-drawer"
            [attr.aria-label]="'shell.openMenu' | transloco"
            (click)="toggleMobileMenu()">
            @if (!mobileMenuOpen()) {
              <svg class="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            } @else {
              <svg class="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            }
          </button>

          <!-- Wheere W logo (recoloured per active tab). -->
          <a routerLink="/" class="flex shrink-0 items-center" aria-label="Wheere">
            <svg
              [attr.height]="22"
              [attr.width]="22 * 1.47"
              fill="none"
              viewBox="0 0 250 170"
              xmlns="http://www.w3.org/2000/svg">
              <path
                [attr.fill]="logoColor()"
                d="M227.681 70.4h-30.136l-11.622 45.305-25.403-97.681C157.716 7.243 148.349 0 137.208 0c-11.139 0-20.507 7.243-23.311 18.025l-25.432 97.802L63.47 18.835C60.687 8.029 51.32.764 40.16.756h-.017c-11.151 0-20.519 7.25-23.316 18.044L0 83.735h30.613l9.515-36.715 27.25 105.739c2.51 9.746 10.96 16.304 21.026 16.317h.027c10.056 0 18.508-6.536 21.038-16.269l27.74-106.674 27.724 106.615c2.532 9.736 10.982 16.269 21.038 16.269h.038c10.071-.017 18.519-6.582 21.023-16.338L228.14 70.4h-.459Z" />
              <path
                [attr.fill]="logoColor()"
                d="M221.142 155.449H216V152h14v3.449h-5.141V169h-3.717v-13.551ZM233 152h4.031l4.47 7.188 4.468-7.188H250v17h-3.692v-11.099l-4.782 7.263h-.099l-4.735-7.189V169H233v-17Z" />
            </svg>
          </a>

          <!-- Inline nav — desktop only (≥ md). -->
          <nav class="hidden gap-0.5 md:flex">
            @for (t of visibleTabs(); track t.id) {
              <a
                [routerLink]="t.link"
                routerLinkActive
                #rla="routerLinkActive"
                class="relative cursor-pointer rounded-md px-3.5 py-2 text-[13px] text-gray-700 transition-colors dark:text-slate-300 dark:hover:bg-slate-700"
                [class.font-bold]="rla.isActive"
                [class.font-medium]="!rla.isActive"
                [style.color]="rla.isActive ? t.color : null">
                {{ t.labelKey | transloco }}
                @if (rla.isActive) {
                  <span
                    class="pointer-events-none absolute -bottom-0.5 left-3 right-3 h-0.5 rounded-full"
                    [style.background]="t.color"></span>
                }
              </a>
            }
          </nav>
        </div>

        <div class="flex shrink-0 items-center gap-2 md:gap-3.5">
          <!-- Offline pill — shown whenever navigator.onLine is false. Stays compact on
               mobile (icon only) and expands with the label on sm+. -->
          @if (!online()) {
            <span
              class="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red-700 dark:bg-red-500/20 dark:text-red-300"
              [title]="'shell.offline' | transloco">
              <span class="size-1.5 rounded-full bg-red-500"></span>
              <span class="hidden sm:inline">{{ 'shell.offline' | transloco }}</span>
            </span>
          }

          <!-- Language cycle — desktop only (drawer hosts the mobile equivalent). -->
          <button
            type="button"
            class="hidden h-8 items-center justify-center gap-0.5 rounded-md border border-gray-200 px-2 text-[11px] font-bold text-gray-700 transition-colors hover:bg-gray-100 sm:inline-flex dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
            [attr.aria-label]="'shell.language' | transloco"
            [title]="'shell.language' | transloco"
            (click)="langService.cycle()">
            {{ langService.shortLabel() }}
          </button>

          <!-- Theme cycle button — system / light / dark. Icon = current state. -->
          <button
            type="button"
            class="hidden size-8 items-center justify-center rounded-full text-gray-600 transition-colors hover:bg-gray-100 sm:inline-flex dark:text-slate-300 dark:hover:bg-slate-700"
            [title]="themeButtonTitle()"
            [attr.aria-label]="themeButtonTitle()"
            (click)="theme.cycle()">
            @switch (themeIcon()) {
              @case ('sun') {
                <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.7">
                  <circle cx="12" cy="12" r="4" />
                  <path
                    stroke-linecap="round"
                    d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                </svg>
              }
              @case ('moon') {
                <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.7">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M21 12.79A9 9 0 1111.21 3a7 7 0 009.79 9.79z" />
                </svg>
              }
              @default {
                <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.7">
                  <circle cx="12" cy="12" r="9" />
                  <path stroke-linecap="round" d="M12 3v18" />
                  <path d="M12 3a9 9 0 010 18z" fill="currentColor" stroke="none" />
                </svg>
              }
            }
          </button>

          <!-- Avatar menu — initials button opens a popup with the user email,
               a copy-support-address action, and logout. PrimeNG p-menu lets us
               mix labels + commands + separators in one model. -->
          <button
            type="button"
            class="bg-primary-500 hover:bg-primary-600 flex h-8 w-8 items-center justify-center rounded-full text-[12px] font-semibold text-white transition-colors focus:outline-none focus:ring-2 focus:ring-pink-300"
            [title]="email() ?? 'Account'"
            (click)="userMenu.toggle($event)">
            {{ initials() }}
          </button>
          <p-menu #userMenu [popup]="true" [model]="avatarMenuItems()" appendTo="body" />
        </div>
      </header>

      <!-- ── Mobile nav drawer ──────────────────────────────────────────────
        Click outside (dark overlay) closes it; clicking a route closes it via
        the NavigationEnd effect below.
        ──────────────────────────────────────────────────────────────────── -->
      @if (mobileMenuOpen()) {
        <div class="fixed inset-x-0 bottom-0 top-14 z-30 md:hidden" (click)="mobileMenuOpen.set(false)">
          <div class="absolute inset-0 bg-black/30"></div>
          <nav
            id="customer-nav-drawer"
            class="relative border-b border-gray-200 bg-white px-3 py-2 shadow-lg dark:border-slate-700 dark:bg-slate-800"
            (click)="$event.stopPropagation()">
            <!-- Drawer language switcher — same control as desktop. -->
            <div class="mb-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                class="inline-flex h-9 items-center gap-1 rounded-md border border-gray-200 px-3 text-[12px] font-bold text-gray-700 dark:border-slate-600 dark:text-slate-200"
                (click)="langService.cycle()">
                <span class="text-[10px] font-normal uppercase tracking-wider text-gray-500 dark:text-slate-400">
                  {{ 'shell.language' | transloco }}
                </span>
                <span>{{ langService.shortLabel() }}</span>
              </button>
              <button
                type="button"
                class="inline-flex h-9 items-center gap-1 rounded-md border border-gray-200 px-3 text-[12px] font-bold text-gray-700 dark:border-slate-600 dark:text-slate-200"
                (click)="theme.cycle()">
                <span class="text-[10px] font-normal uppercase tracking-wider text-gray-500 dark:text-slate-400">
                  {{ 'shell.theme' | transloco }}
                </span>
                <span>{{ themeButtonTitle() }}</span>
              </button>
            </div>
            <ul class="flex flex-col">
              @for (t of visibleTabs(); track t.id) {
                <li>
                  <a
                    [routerLink]="t.link"
                    routerLinkActive
                    #rla="routerLinkActive"
                    class="flex min-h-[44px] items-center gap-2 rounded-md px-3 py-3 text-[14px] text-gray-700 transition-colors active:bg-gray-100 dark:text-slate-300 dark:active:bg-slate-700"
                    [class.font-bold]="rla.isActive"
                    [class.font-medium]="!rla.isActive"
                    [style.color]="rla.isActive ? t.color : null">
                    <span class="inline-block size-2.5 rounded-full" [style.background]="t.color"></span>
                    {{ t.labelKey | transloco }}
                  </a>
                </li>
              }
            </ul>
          </nav>
        </div>
      }

      <main class="relative z-[1] flex-1">
        <ng-content />
      </main>

      <!-- Global bug-report launcher — floating bottom-right. The button itself
           always renders; the underlying BugReportService only succeeds when the
           consuming app provides USERMANAGER_BASE_PATH (see wheere-customer
           app.config.ts: it provides the token only when environment.api.user
           is set, otherwise an HTTP POST to undefined fails fast). -->
      <lib-bug-report-button />
    </div>
  `,
})
export class CustomerShellComponent {
  protected auth = inject(AuthStore);
  protected theme = inject(ThemeService);
  protected langService = inject(LanguageService);
  #router = inject(Router);
  #keycloak = inject(KeycloakService);
  #transloco = inject(TranslocoService);
  #message = inject(MessageService);

  /** Support address shown in the account menu — not a secret. */
  private static readonly SUPPORT_EMAIL = 'support-technique@wheere.com';

  protected fullname = this.auth.fullname;
  protected email = this.auth.email;

  /** Mobile drawer open state — desktop ignores it (CSS-hidden under md). */
  protected readonly mobileMenuOpen = signal(false);

  /** Browser-reported online state. Drives the offline pill in the header.
   * Initialised from {@code navigator.onLine}; toggled by the {@code online} /
   * {@code offline} window events. SSR-safe. */
  protected readonly online = signal<boolean>(typeof navigator !== 'undefined' ? navigator.onLine : true);

  /** Tracks NavigationEnd events so the drawer auto-closes after a route change.
   * Without this, tapping a link on mobile opens the new page but leaves the panel
   * floating on top of the content. */
  readonly #routeChange = toSignal(this.#router.events.pipe(filter(e => e instanceof NavigationEnd)));

  protected toggleMobileMenu(): void {
    this.mobileMenuOpen.update(v => !v);
  }

  /** Hex colour for the active tab — drives the W logo recolouring. */
  protected logoColor = computed(() => this.activeTab()?.color ?? '#5000CD');

  /** Tabs visible for the current user. Subscribes to the active lang so
   * Transloco re-renders labels on FR/EN toggle (the pipe handles the actual
   * lookup, but we need the {@code computed} to recompute on lang change).
   *
   * <p>2026-05-29 — Users tab removed. User provisioning is owned by the
   * integrator app (the customer is now strictly a viewer for end users). */
  protected visibleTabs = computed<CustomerNavTab[]>(() => {
    this.langService.active(); // dep — re-evaluate on lang change
    const tabs: CustomerNavTab[] = [
      { id: 'sites', labelKey: 'shell.nav.sites', color: '#5000CD', link: '/sites', visibleFor: [] },
    ];
    return tabs.filter(t => t.visibleFor.length === 0 || this.auth.hasAnyRole(t.visibleFor));
  });

  /** Tab matching the current URL — used to recolour the W. */
  protected activeTab = computed<CustomerNavTab | undefined>(() => {
    const url = this.#router.url;
    return this.visibleTabs().find(t => url.startsWith(t.link));
  });

  /** Icon name for the theme button — shows the current resolved icon (sun =
   * light, moon = dark, half = system). */
  protected themeIcon = computed<'sun' | 'moon' | 'auto'>(() => {
    const m = this.theme.mode();
    if (m === 'system') return 'auto';
    return m === 'dark' ? 'moon' : 'sun';
  });

  /** Hover/aria label for the theme button. */
  protected themeButtonTitle = computed<string>(() => {
    this.langService.active();
    const m = this.theme.mode();
    const key = m === 'system' ? 'shell.themeSystem' : m === 'light' ? 'shell.themeLight' : 'shell.themeDark';
    return this.#transloco.translate(key);
  });

  /** Items shown in the avatar popup: email label, separator, copy-support,
   * logout. Mirrors integrator's layout. */
  protected avatarMenuItems = computed<MenuItem[]>(() => {
    this.langService.active();
    return [
      {
        label: this.auth.email() ?? this.#transloco.translate('shell.account'),
        disabled: true,
        styleClass: 'wh-user-menu-email',
      },
      { separator: true },
      {
        label: `${this.#transloco.translate('shell.support')} : ${CustomerShellComponent.SUPPORT_EMAIL}`,
        icon: 'pi pi-copy',
        command: () => this.copySupportEmail(),
      },
      {
        label: this.#transloco.translate('shell.logout'),
        icon: 'pi pi-sign-out',
        command: () => this.onLogout(),
      },
    ];
  });

  /** Initials computed from the fullname; falls back to the email handle. */
  protected initials = computed<string>(() => {
    const full = this.fullname()?.trim();
    if (full && !full.includes('undefined')) {
      const parts = full.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      if (parts[0]) return parts[0].slice(0, 2).toUpperCase();
    }
    const mail = this.email();
    if (mail) return mail.slice(0, 2).toUpperCase();
    return 'WH';
  });

  /** Copy the support address to the clipboard + confirm with a toast. No mail is sent. */
  protected copySupportEmail(): void {
    const done = () =>
      this.#message.add({
        severity: 'success',
        summary: this.#transloco.translate('shell.supportCopied'),
        detail: CustomerShellComponent.SUPPORT_EMAIL,
      });
    if (navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(CustomerShellComponent.SUPPORT_EMAIL).then(done, () => undefined);
    } else {
      done();
    }
  }

  onLogout(): void {
    this.#keycloak.logout();
  }

  constructor() {
    // Start capturing console output as soon as the shell mounts, so any later
    // bug report ships the recent logs + uncaught errors as attachment.
    inject(ConsoleCaptureService).start();

    // Track navigator.onLine so the header can surface an offline pill. The
    // listeners are global and tied to the SignalStore's lifetime via the root
    // injector — no cleanup needed (the shell is the root component).
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.online.set(true));
      window.addEventListener('offline', () => this.online.set(false));
    }

    // Close the mobile drawer whenever the URL changes — clicking a link in the drawer
    // triggers a NavigationEnd, the effect runs, the panel disappears.
    //
    // mobileMenuOpen() is read inside untracked() so the effect's only
    // dependency is #routeChange — otherwise toggling the signal would re-fire
    // the effect, close the drawer the instant it opens.
    effect(
      () => {
        this.#routeChange();
        if (untracked(() => this.mobileMenuOpen())) this.mobileMenuOpen.set(false);
      },
      { allowSignalWrites: true }
    );
  }
}
